import type { SentCard } from '@nudge/shared';
import { QuickResponse } from './QuickResponse';
import { useAuthStore } from '../../stores/auth';
import { useCircleStore } from '../../stores/circle';
import { useCardsStore } from '../../stores/cards';

export function ActiveCard({ card }: { card: SentCard }) {
  const me = useAuthStore((s) => s.user);
  const members = useCircleStore((s) => s.members);
  const clearCard = useCardsStore((s) => s.clearCard);

  const isMine = card.sender_id === me?.id;
  const senderMember = members.find((m) => m.user_id === card.sender_id);
  const senderName = senderMember?.display_name ?? card.sender?.display_name ?? 'Someone';
  const showSenderName = members.length > 2 && !isMine;
  const isFirm = card.intensity === 'firm';
  const acks = card.acknowledgments ?? [];
  const myAck = acks.find((a) => a.user_id === me?.id);
  const isPending = card.id.startsWith('pending-');

  // Build a lookup map so the ack list doesn't call members.find() per acknowledgment
  const memberById = new Map(members.map((m) => [m.user_id, m]));

  return (
    <div
      className={`rounded-2xl border p-4 ${
        isFirm ? 'border-amber/50 bg-amber/10' : 'border-border-subtle bg-card'
      } ${isPending ? 'opacity-70' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl" aria-hidden>{card.template?.icon ?? '💬'}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-text-muted">
            {showSenderName && <span>{senderName}</span>}
            {isFirm && <span className="rounded-full bg-amber/30 px-2 py-0.5 text-[10px] font-medium text-amber">FIRM</span>}
            {isPending && <span className="text-[10px]">Sending…</span>}
          </div>
          <div className="mt-0.5 text-base font-medium text-text-primary">{card.template?.label ?? 'Card'}</div>
          {card.template?.subtext && (
            <div className="text-sm text-text-muted">{card.template.subtext}</div>
          )}
        </div>
        {isMine && !isPending && (
          <button onClick={() => clearCard(card.id)}
            className="rounded-lg border border-border-subtle bg-surface px-3 py-1.5 text-xs text-text-muted">
            Clear
          </button>
        )}
      </div>

      {acks.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {acks.map((a) => {
            const m = memberById.get(a.user_id);
            const label = a.response ? `"${a.response}"` : 'Got it ✓';
            return (
              <span key={a.id} className="rounded-full bg-surface px-2.5 py-1 text-xs text-text-muted">
                {m ? `${m.display_name}: ` : ''}{label}
              </span>
            );
          })}
        </div>
      )}

      {!isMine && !myAck && !isPending && <QuickResponse sentCardId={card.id} />}
    </div>
  );
}
